import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { evaluatePermission, PermissionRule } from '../../utils/permissionEngine';
import AccessDenied from '../../pages/Utility/AccessDenied';

interface PageGuardProps {
  children: React.ReactNode;
  /**
   * Required roles (OR logic - user needs at least one)
   */
  requiredRoles?: string[];
  /**
   * Required capabilities (AND logic - user needs all)
   */
  requiredCapabilities?: string[];
  /**
   * Required capabilities with OR logic (user needs at least one)
   */
  anyCapability?: string[];
  /**
   * Custom access denied component (optional)
   */
  accessDeniedComponent?: React.ReactNode;
}

/**
 * PageGuard component for page-level protection
 * 
 * Wraps page content and shows AccessDenied if user lacks permission.
 * Supports both WP and business capabilities.
 * 
 * @example
 * <PageGuard requiredCapabilities={['edit_orders', 'business_booking_edit_details']}>
 *   <BookingEditPage />
 * </PageGuard>
 */
const PageGuard: React.FC<PageGuardProps> = ({
  children,
  requiredRoles,
  requiredCapabilities,
  anyCapability,
  accessDeniedComponent,
}) => {
  const { permissions, loading } = useAuth();

  // Show loading state while fetching permissions
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Build permission rule
  const rule: PermissionRule = {
    roles: requiredRoles,
    capabilities: requiredCapabilities,
    anyCapability: anyCapability,
  };

  // Evaluate permission
  const hasPermission = evaluatePermission(rule, permissions);

  if (!hasPermission) {
    return accessDeniedComponent || <AccessDenied />;
  }

  return <>{children}</>;
};

export default PageGuard;
